/**
 * carriedCriteria — the PURE rule behind "run these criteria on another people
 * list" (PRD `2026-09-10-ai-review-criteria-reuse`, FR3/FR4/FR10).
 *
 * No I/O, no React: the query that fetches the source review lives in
 * `AiReviewPage`, this module only decides what the form should hold once its
 * criteria arrive. Same split as `outputFilename.ts` and `previewRows.ts`.
 *
 * APPEND, not replace (product decision, 2026-09-10). A recruiter who has
 * already typed something did not ask for it to be thrown away, and the two
 * lists compose into one rubric — which is the whole point of carrying them.
 */
import type { Criterion } from "./CriteriaPanel";
import { reconcileCriteria } from "./criteriaRows";

export interface MergeCarriedCriteriaResult {
  criteria: Criterion[];
  /**
   * How many arrivals the CAP refused — never the ones skipped as duplicates.
   *
   * It drives one sentence — "N criteria did not fit" — which is about SPACE. A
   * duplicate was not squeezed out of the form; it was already in it, and
   * saying otherwise would send the recruiter looking for a criterion that is
   * on screen.
   */
  dropped: number;
}

/**
 * Merge a source review's criteria into the ones already in the form.
 *
 * The rules, and why each is here:
 *
 *  - **Blank rows are dropped** from the TEXT. The empty form is seeded with
 *    one empty row (`emptyCriteria()`), so carrying onto an untouched form
 *    would otherwise strand it above the arrivals.
 *  - **No row is reminted.** The texts are written into the existing rows BY
 *    POSITION (`reconcileCriteria`), so the untouched form's single blank row
 *    is FILLED by the first arrival rather than replaced by a new one, and a
 *    typed row keeps its id. Reminting an id remounts that row's field, which
 *    drops focus and the caret (PRD 2026-10-06, C1). Only rows beyond the
 *    form's current count draw from `nextId`. A blank row BETWEEN typed ones
 *    makes the texts below it shift up one node; that is the price of never
 *    moving a node, which would blur a focused one.
 *  - **Duplicates are skipped**, compared on the TRIMMED text and
 *    case-SENSITIVELY. This is not a style choice: `AiReviewPage`'s
 *    `hasDuplicateCriteria` builds a `Set` over exactly those trimmed strings
 *    and DISABLES the run when it collides, and the backend validates the same
 *    way. Folding case here would discard a criterion the server would have
 *    accepted; not deduplicating at all would hand back a form that cannot run.
 *  - **The result is capped**, because the backend's `criteria` field is
 *    `max_length=8` and nothing else clamps a programmatic write —
 *    `CriteriaPanel` only disables its "Add criterion" button.
 *  - **The result is never empty.** An absent row means the recruiter has
 *    nothing to type into; the form's own invariant, kept here rather than at
 *    each call site.
 *
 * @param existing  what the form holds now (not mutated)
 * @param incoming  the source review's criteria, in its own order
 * @param max       the cap — `MAX_CRITERIA`, mirroring the backend's
 * @param nextId    id factory for the rows beyond the form's current count
 */
export function mergeCarriedCriteria(
  existing: readonly Criterion[],
  incoming: readonly string[],
  max: number,
  nextId: () => string
): MergeCarriedCriteriaResult {
  const kept: string[] = [];
  const seen = new Set<string>();

  for (const criterion of existing) {
    const text = criterion.text.trim();
    if (text.length === 0) continue;
    if (seen.has(text)) continue;
    seen.add(text);
    kept.push(text);
  }

  let dropped = 0;
  for (const arrival of incoming) {
    const text = arrival.trim();
    if (text.length === 0) continue;
    if (seen.has(text)) continue;
    // The cap is counted only from here on: an arrival that never competed for
    // a slot (blank, or already present) is not a victim of the cap.
    if (kept.length >= max) {
      dropped += 1;
      continue;
    }
    seen.add(text);
    kept.push(text);
  }

  const texts = kept.length > 0 ? kept : [""];
  return { criteria: reconcileCriteria(existing, texts, nextId), dropped };
}
